import { get as idbGet, set as idbSet, del as idbDel, keys as idbKeys } from 'idb-keyval';
import type { ProjectFile } from '@archer/shared';
import type { ProjectSummary, StorageAdapter } from './types';

const INDEX_KEY = 'archer:projects:index';
const FILE_KEY = (id: string): string => `archer:projects:${id}`;

interface IndexEntry {
  id: string;
  name: string;
  updatedAt: string;
  thumbnail?: string;
}

const readIndex = async (): Promise<IndexEntry[]> => {
  const raw = await idbGet<IndexEntry[]>(INDEX_KEY);
  return raw ?? [];
};

const writeIndex = async (entries: IndexEntry[]): Promise<void> => {
  await idbSet(INDEX_KEY, entries);
};

export class BrowserStorageAdapter implements StorageAdapter {
  readonly kind = 'browser' as const;
  readonly label = 'Browser storage';

  async list(): Promise<ProjectSummary[]> {
    const entries = await readIndex();
    return entries.map((e) => ({
      id: e.id,
      name: e.name,
      ownerId: 'local',
      thumbnail: e.thumbnail,
      schemaVersion: 1,
      createdAt: e.updatedAt,
      updatedAt: e.updatedAt,
      storageKind: 'browser',
      storageRef: e.id,
    }));
  }

  async load(storageRef: string): Promise<ProjectFile> {
    const file = await idbGet<ProjectFile>(FILE_KEY(storageRef));
    if (!file) throw new Error(`project ${storageRef} not found in browser storage`);
    return file;
  }

  async save(storageRef: string, file: ProjectFile): Promise<void> {
    await idbSet(FILE_KEY(storageRef), file);
    const entries = await readIndex();
    const filtered = entries.filter((e) => e.id !== storageRef);
    filtered.unshift({
      id: storageRef,
      name: file.name,
      updatedAt: file.updatedAt,
      thumbnail: file.thumbnail,
    });
    await writeIndex(filtered);
  }

  async remove(storageRef: string): Promise<void> {
    await idbDel(FILE_KEY(storageRef));
    const entries = await readIndex();
    await writeIndex(entries.filter((e) => e.id !== storageRef));
  }
}
